import type { AudioBuses } from './buses';

const NOTE_SEMITONE: Record<string, number> = { C: 0, D: 2, E: 4, F: 5, G: 7, A: 9, B: 11 };

/** "A2", "C#4" -> Hz, via standard 12-tone equal temperament off A4=440Hz. */
function noteToHz(note: string): number {
  const m = /^([A-G])(#?)(\d)$/.exec(note);
  if (!m) return 440;
  const [, letter, sharp, octaveStr] = m;
  const semitone = NOTE_SEMITONE[letter] + (sharp ? 1 : 0);
  const midi = (parseInt(octaveStr, 10) + 1) * 12 + semitone;
  return 440 * Math.pow(2, (midi - 69) / 12);
}

// The Astoria motif — a short, looping 2-voice chiptune riff evoking C02's
// concept-board mood (Greek-diner/subway-block NYC), not a quotation of
// anything real (Classic's own copyright law: original melodies only).
// 32 sixteenth-note steps = 2 bars at 4/4; `null` = rest. BASS and LEAD must
// stay the SAME length — the scheduler indexes both by one shared step
// counter (see MusicSequencer.scheduleAhead).
const BASS: (string | null)[] = [
  'A2', null, null, 'A2', null, null, 'E2', null,
  'E2', null, null, 'E2', null, null, 'F2', null,
  'F2', null, null, 'F2', null, null, 'E2', null,
  'D2', null, null, 'D2', null, null, 'E2', null,
];
const LEAD: (string | null)[] = [
  null, null, 'E4', null, 'C4', null, null, 'B3',
  null, 'A3', null, null, 'C4', null, 'E4', null,
  null, null, 'D4', null, 'C4', null, null, 'B3',
  null, 'A3', null, null, null, 'G3', null, null,
];

const BPM = 100;
const STEP_SECONDS = 60 / BPM / 4; // sixteenth notes
const SCHEDULE_AHEAD_SECONDS = 0.1;
const LOOKAHEAD_MS = 25;

/**
 * A real step sequencer using the standard Web Audio "precise scheduling"
 * pattern (schedule notes into a short lookahead window on a timer, using
 * `AudioContext.currentTime` for the actual note-start times rather than
 * trusting `setTimeout`'s own jitter) — PLAN-CITY §5's "chiptune sequencer
 * port," not a static loop. Bass on `triangle` (a soft, sustained low
 * voice), lead on `square` (the brighter, shorter melodic voice) — the same
 * two-oscillator-type contrast Classic's own REGION_MUSIC motifs use.
 */
export class MusicSequencer {
  private timer: ReturnType<typeof setInterval> | null = null;
  private nextStepTime = 0;
  private stepIndex = 0;
  private readonly bassHz: (number | null)[];
  private readonly leadHz: (number | null)[];

  constructor(private readonly buses: AudioBuses) {
    this.bassHz = BASS.map((n) => (n ? noteToHz(n) : null));
    this.leadHz = LEAD.map((n) => (n ? noteToHz(n) : null));
  }

  get isPlaying(): boolean {
    return this.timer !== null;
  }

  /** No-op if the buses aren't unlocked yet, or already playing. Call again after `unlock()` succeeds. */
  start(): void {
    if (this.timer !== null) return;
    const ctx = this.buses.context;
    if (!ctx) return;
    this.stepIndex = 0;
    this.nextStepTime = ctx.currentTime + 0.05;
    this.timer = setInterval(this.scheduleAhead, LOOKAHEAD_MS);
  }

  stop(): void {
    if (this.timer === null) return;
    clearInterval(this.timer);
    this.timer = null;
  }

  private scheduleAhead = (): void => {
    const ctx = this.buses.context;
    const bus = this.buses.musicBus;
    if (!ctx || !bus) return;
    while (this.nextStepTime < ctx.currentTime + SCHEDULE_AHEAD_SECONDS) {
      const bassFreq = this.bassHz[this.stepIndex];
      if (bassFreq !== null) this.playNote(ctx, bus, bassFreq, this.nextStepTime, STEP_SECONDS * 1.7, 'triangle', 0.5);
      const leadFreq = this.leadHz[this.stepIndex];
      if (leadFreq !== null) this.playNote(ctx, bus, leadFreq, this.nextStepTime, STEP_SECONDS * 0.9, 'square', 0.22);
      this.nextStepTime += STEP_SECONDS;
      this.stepIndex = (this.stepIndex + 1) % BASS.length;
    }
  };

  private playNote(ctx: AudioContext, bus: GainNode, freq: number, time: number, duration: number, type: OscillatorType, peakGain: number): void {
    const osc = ctx.createOscillator();
    osc.type = type;
    osc.frequency.value = freq;
    const env = ctx.createGain();
    env.gain.setValueAtTime(0, time);
    env.gain.linearRampToValueAtTime(peakGain, time + 0.01);
    env.gain.exponentialRampToValueAtTime(0.001, time + duration);
    osc.connect(env).connect(bus);
    osc.start(time);
    osc.stop(time + duration + 0.02);
    osc.onended = () => {
      osc.disconnect();
      env.disconnect();
    };
  }
}
